import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Cookie, Settings, Shield, Eye, Database, Globe } from 'lucide-react'

const CookiePolicy: React.FC = () => {
  const cookieTypes = [
    {
      title: '必要Cookie',
      icon: Shield,
      description: '这些Cookie对于网站正常运行是必需的，无法关闭。',
      examples: [
        '会话管理Cookie',
        '安全认证Cookie',
        '负载均衡Cookie',
        '用户偏好设置Cookie'
      ],
      purpose: '确保网站基本功能正常运行，维护用户会话安全'
    },
    {
      title: '功能Cookie',
      icon: Settings,
      description: '这些Cookie增强网站功能，但关闭不会影响基本使用。',
      examples: [
        '语言选择Cookie',
        '主题设置Cookie',
        '购物车状态Cookie',
        '表单数据临时保存Cookie'
      ],
      purpose: '记住您的偏好设置，提供个性化体验'
    },
    {
      title: '分析Cookie',
      icon: Eye,
      description: '这些Cookie帮助我们了解网站使用情况，改进服务质量。',
      examples: [
        'Google Analytics Cookie',
        '页面访问统计Cookie',
        '用户行为分析Cookie',
        '性能监控Cookie'
      ],
      purpose: '分析网站使用情况，优化用户体验和网站性能'
    },
    {
      title: '营销Cookie',
      icon: Globe,
      description: '这些Cookie用于提供相关广告和营销内容。',
      examples: [
        '广告定向Cookie',
        '社交媒体集成Cookie',
        '再营销Cookie',
        '转化跟踪Cookie'
      ],
      purpose: '向您展示相关广告，衡量广告效果'
    }
  ]

  return (
    <>
      <Helmet>
        <title>Cookie政策 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited Cookie使用政策，符合香港和欧盟法规要求" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Cookie政策
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                最后更新：2024年1月1日 | 生效日期：2024年1月1日
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-2xl mx-auto">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>重要提示：</strong>本Cookie政策说明我们如何使用Cookie和类似技术，符合香港和欧盟相关法规要求。
                </p>
              </div>
            </div>

            <div className="space-y-8">
              {/* 什么是Cookie */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-4">
                    <Cookie className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    什么是Cookie？
                  </h2>
                </div>
                <div className="space-y-3">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Cookie是当您访问网站时存储在您设备上的小型文本文件。它们帮助网站记住您的信息，如登录状态、语言偏好等。
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    我们使用Cookie来改善您的浏览体验，分析网站使用情况，并提供个性化内容。
                  </p>
                </div>
              </motion.div>

              {/* Cookie类型 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
              >
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                  我们使用的Cookie类型
                </h2>
                <div className="grid gap-6">
                  {cookieTypes.map((type, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                          <type.icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {type.title}
                        </h3>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mb-3">
                        {type.description}
                      </p>
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                          用途：
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {type.purpose}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                          示例：
                        </h4>
                        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          {type.examples.map((example, exampleIndex) => (
                            <li key={exampleIndex} className="flex items-center">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Cookie管理 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-4">
                    <Settings className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Cookie管理
                  </h2>
                </div>
                <div className="space-y-4">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    您可以通过以下方式管理Cookie：
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">1</span>
                      </span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">浏览器设置</h4>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          大多数浏览器允许您控制Cookie设置。您可以选择接受所有Cookie、拒绝所有Cookie或选择性地接受某些Cookie。
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">2</span>
                      </span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">Cookie同意横幅</h4>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          首次访问我们的网站时，您会看到Cookie同意横幅，可以选择接受或拒绝非必要Cookie。
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">3</span>
                      </span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">联系我们</h4>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">
                          如果您对Cookie使用有任何疑问，可以通过contact@deeplink.hk联系我们。
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* 第三方Cookie */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
              >
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-4">
                    <Globe className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    第三方Cookie
                  </h2>
                </div>
                <div className="space-y-3">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    我们可能使用第三方服务，这些服务可能会设置自己的Cookie：
                  </p>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                      <strong>Google Analytics：</strong> 用于网站分析
                    </li>
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                      <strong>社交媒体插件：</strong> 用于社交分享功能
                    </li>
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                      <strong>支付服务：</strong> 用于处理支付交易
                    </li>
                    <li className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></span>
                      <strong>地图服务：</strong> 用于显示位置信息
                    </li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    这些第三方服务有自己的隐私政策和Cookie政策，我们建议您查看它们的政策。
                  </p>
                </div>
              </motion.div>

              {/* 联系我们 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  联系我们
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  如果您对本Cookie政策有任何疑问，请通过以下方式联系我们：
                </p>
                <div className="space-y-2">
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>电邮：</strong> privacy@deeplink.hk
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>电话：</strong> +852 1234-5678
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>地址：</strong> 香港中环国际金融中心二期
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default CookiePolicy